import { useEffect, useState } from "react";
import { X } from "lucide-react";
import client from "../lib/api";

/**
 * YearTouch.
 *
 * A single gentle door at day 180. Truly once, then never. Care-
 * rail aware; suppressed by the hard outreach opt-out. Renders as
 * a small warm banner on Home when `is_due=true`. The person can
 * accept (opens a fresh full read in a new tab / route), decline
 * softly (dismiss, which fires and permanently retires the touch),
 * or set the hard opt-out so nothing proactive ever surfaces again.
 */
export function YearTouch({ pledgeId, product }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showOptOut, setShowOptOut] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!pledgeId) return () => { alive = false; };
    client.get(`/accountability/pledge/${pledgeId}/year-touch`)
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [pledgeId]);

  const acceptFreshRead = async () => {
    setBusy(true);
    try {
      // Fire the touch first so it never re-surfaces.
      await client.post(`/accountability/pledge/${pledgeId}/year-touch/fire`);
      // Start a fresh FULL read on the same product.
      const { data } = await client.post("/demo/session/start", {
        product: product || "start", intake: {}, mode: "full",
      });
      window.location.href = `/session/${data.session_id}`;
    } catch (_e) { setBusy(false); }
  };

  const softDecline = async () => {
    setBusy(true);
    try {
      await client.post(`/accountability/pledge/${pledgeId}/year-touch/fire`);
      setStatus({ ...status, is_due: false, already_fired: true });
    } finally { setBusy(false); }
  };

  const hardOptOut = async () => {
    setBusy(true);
    try {
      await client.post("/accountability/me/outreach-opt-out", { value: true });
      await client.post(`/accountability/pledge/${pledgeId}/year-touch/fire`);
      setStatus({ ...status, is_due: false, opted_out: true });
      setShowOptOut(false);
    } finally { setBusy(false); }
  };

  if (!status?.is_due) return null;

  return (
    <div className="k-warm-card mt-4 p-4" data-testid="year-touch">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="k-label">a gentle door</div>
        <button onClick={softDecline} className="k-btn k-btn-ghost !p-2" data-testid="year-touch-close">
          <X size={14} />
        </button>
      </div>
      <p className="klareet-serif text-sm mb-2" style={{ lineHeight: 1.5 }} data-testid="year-touch-message">
        {status.message}
      </p>
      {status.trial_note && (
        <p className="k-quiet !text-xs mb-3" data-testid="year-touch-trial-note">
          {status.trial_note}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <button
          onClick={() => setShowOptOut(true)}
          disabled={busy}
          className="k-btn k-btn-ghost !text-xs"
          data-testid="year-touch-opt-out-btn"
        >
          do not reach out again
        </button>
        <button
          onClick={softDecline}
          disabled={busy}
          className="k-btn k-btn-ghost !text-xs"
          data-testid="year-touch-decline"
        >
          not now
        </button>
        <button
          onClick={acceptFreshRead}
          disabled={busy}
          className="k-btn k-btn-primary !text-xs"
          data-testid="year-touch-accept"
        >
          {busy ? "starting" : "let's do a fresh read"}
        </button>
      </div>

      {showOptOut && (
        <div className="k-warm-card mt-3 p-3" data-testid="year-touch-opt-out-panel">
          <p className="text-sm klareet-serif" style={{ lineHeight: 1.5 }}>
            this will stop every proactive touch, on every pledge you own. you can still open klareet whenever you like.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => setShowOptOut(false)}
              disabled={busy}
              className="k-btn k-btn-ghost !text-xs"
              data-testid="year-touch-opt-out-cancel"
            >
              cancel
            </button>
            <button
              onClick={hardOptOut}
              disabled={busy}
              className="k-btn k-btn-primary !text-xs"
              data-testid="year-touch-opt-out-confirm"
            >
              {busy ? "saving" : "confirm, do not reach out"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
